/*  MUI  */
import { Paper, TableCell } from '@mui/material';

/*  Shared  */
import {
    ApplicationBubbles,
    Sources,
    UserBubbles
} from 'application/Shared/Components/Avatars';
import { DateFormat } from 'application/Shared/Components/Core';
import { ListUserAccounts } from 'application/Shared/Components/ListUserAccounts';
import { ManagedTable } from 'application/Shared/Components/Table';
import { Helpers } from 'application/Shared/Helpers';

/*  React  */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

const csvFields = (t) => [
    { key: 'username', label: t('Common.account') },
    {
        key: 'personal',
        label: t('Reports.AccountsInventory.personal')
    },
    {
        key: 'work',
        label: t('Reports.AccountsInventory.work')
    },
    {
        key: 'shared',
        label: t('Reports.AccountsInventory.shared')
    },
    {
        key: 'service',
        label: t('Reports.AccountsInventory.service')
    },
    { key: 'users.length', label: t('UsersAccountsAndDevices.numberOfUsers') },
    {
        key: 'usersList',
        label: t('Common.users'),
        rowKeyAndDataPath: 'users:name',
        transform: 'listExtract'
    },
    {
        key: 'sources.AZURE_SIGN_IN',
        label: t('Common.microsoftEntraID.loginFound')
    },
    {
        key: 'sources.GOOGLE_SIGN_IN',
        label: t('Common.googleLoginFound')
    },
    {
        key: 'sources.LOGIN',
        label: t('Common.cloudLoginFound')
    },
    { key: 'sources.GOOGLE_SSO', label: t('Common.gwsSSOFound') },
    { key: 'sources.MICROSOFT_SSO', label: t('Common.microsoftSSOFound') },
    {
        key: 'totalLogins',
        label: t('UsersAccountsAndDevices.totalLogins')
    },
    {
        key: 'firstUsed',
        label: t('UsersAccountsAndDevices.discoveredDate')
    },
    { key: 'lastUsed', label: t('UsersAccountsAndDevices.lastTimeLogin') },
    {
        key: 'applications.length',
        label: t('UsersAccountsAndDevices.numberOfApps')
    },
    {
        key: 'applicationsList',
        label: t('Common.appsAccessed'),
        rowKeyAndDataPath: 'applications:name',
        transform: 'listExtract'
    }
];

const headers = (t) => [
    {
        field: 'username',
        renderHeader: t('Common.account'),
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'username',
        width: '20%'
    },
    {
        field: 'user.id',
        renderHeader: t('Common.users'),
        sortable: false,
        width: '15%'
    },
    {
        align: 'left',
        field: 'source',
        renderHeader: t('Common.source'),
        sortable: false,
        width: '15%'
    },
    {
        field: 'totalLogins',
        renderHeader: t('UsersAccountsAndDevices.totalLogins'),
        sortable: true,
        sortableFieldType: 'number',
        sortableKey: 'totalLogins',
        width: '10%'
    },
    {
        field: 'firstUsed',
        renderHeader: t('UsersAccountsAndDevices.discoveredDate'),
        sortable: true,
        sortableFieldType: 'date',
        sortableKey: 'firstUsed',
        width: '15%'
    },
    {
        field: 'lastUsed',
        renderHeader: t('UsersAccountsAndDevices.lastTimeLogin'),
        sortable: true,
        sortableFieldType: 'date',
        sortableKey: 'lastUsed',
        width: '15%'
    },
    {
        field: 'applications.length',
        renderHeader: t('Common.applications'),
        sortable: true,
        sortableFieldType: 'number',
        sortableKey: 'applications.length',
        width: '10%'
    }
];

const RenderRow = ({ row }) => {
    const {
        applications,
        firstUsed,
        lastUsed,
        personal,
        service,
        shared,
        totalLogins,
        username,
        users,
        work
    } = row;
    const sources = Helpers.getSourcesLoginForAccounts(row.sources);

    const accounts = [{ personal, service, shared, username, work }];

    return (
        <>
            <TableCell>
                <ListUserAccounts accounts={accounts} />
            </TableCell>
            <TableCell>
                <UserBubbles align="left" max={3} users={users} dataIsUser />
            </TableCell>
            <TableCell>
                <Sources sources={sources} max={3} />
            </TableCell>
            <TableCell>{totalLogins}</TableCell>
            <TableCell>
                <DateFormat date={firstUsed} noTimeString noTz />
            </TableCell>
            <TableCell>
                <DateFormat date={lastUsed} noTimeString noTz />
            </TableCell>
            <TableCell>
                <ApplicationBubbles
                    max={3}
                    applications={applications}
                    tooltipPlacement="left"
                />
            </TableCell>
        </>
    );
};

const searchFields = ['username'];

export const AdminUsersAccountsTable = ({ setData }) => {
    const { clientId } = useParams();
    const { t } = useTranslation();

    const parameters = useMemo(
        () => ({
            compactUsernames: true,
            platformTenantId: clientId
        }),
        [clientId]
    );

    const tableCsvFields = useMemo(() => csvFields(t), [t]);

    const tableHeaders = useMemo(() => headers(t), [t]);

    return (
        <Paper
            elevation={0}
            variant="outlined"
            data-testid="adminUsersAccountsTable"
        >
            <ManagedTable
                csvExport
                csvExportFields={tableCsvFields}
                csvExportFileName="admin_users_accounts"
                data-trackid="accountsExportBtn"
                headers={tableHeaders}
                parameters={parameters}
                renderBody={(row) => <RenderRow row={row} />}
                renderFooter
                rowsApi="/applicationAccounts"
                rowsApiPreload
                rowsPerPage="dynamic"
                searchEnabled
                searchFields={searchFields}
                setDataFn={setData}
                sortDirection="ASC"
                sortFieldType="string"
                sortKey="username"
            />
        </Paper>
    );
};
